import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Stack,
  IconButton,
  Chip,
  Tooltip,
  Link,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { 
  fetchScheduledMeetings, 
  deleteScheduledMeeting,
  updateScheduledMeeting
} from "api/meet/meetAPI";
import { ScheduledMeeting, MeetingStatus } from "types/meetings";
import { useNavigate } from "react-router-dom";
import EditMeetingModal from "./EditMeetingModal";
import useNotificationProvider from "hooks/notificationBar/useNotificationProvider";

// Helper function to calculate meeting status and join availability
const calculateMeetingStatus = (meeting: ScheduledMeeting): MeetingStatus => {
  const now = new Date();
  const startTime = new Date(meeting.meeting_start_datetime);
  const endTime = new Date(startTime.getTime() + meeting.meeting_duration * 60000);
  
  const timeUntilStart = Math.floor((startTime.getTime() - now.getTime()) / (1000 * 60));
  const isOverdue = now > endTime;
  
  let status: 'upcoming' | 'can_join' | 'in_progress' | 'completed' | 'cancelled';
  let canJoin = true;
  
  if (meeting.status === 'cancelled') {
    status = 'cancelled';
    canJoin = false;
  } else if (isOverdue) {
    status = 'completed';
    canJoin = false;
  } else if (timeUntilStart <= 10 && timeUntilStart >= 0) {
    status = 'can_join';
  } else if (timeUntilStart < 0 && !isOverdue) {
    status = 'in_progress';
  } else {
    status = 'upcoming';
  }
  
  return {
    canJoin,
    timeUntilStart,
    isOverdue,
    status
  };
};

// Helper function to format duration
const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

// Helper function to format recurrence
const formatRecurrence = (recurrence: string): string => {
  switch (recurrence) {
    case 'none': return 'One-time';
    case 'daily': return 'Daily';
    case 'weekly': return 'Weekly';
    case 'monthly': return 'Monthly';
    case 'yearly': return 'Yearly';
    default: return 'One-time';
  }
};

// Helper function to get status color
const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (status) {
    case 'upcoming': return 'info';
    case 'can_join': return 'success';
    case 'in_progress': return 'primary';
    case 'completed': return 'default';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

interface ScheduledMeetingsProps {
  refreshTrigger?: number; // Optional prop to trigger refresh
}

const ScheduledMeetings: React.FC<ScheduledMeetingsProps> = ({ refreshTrigger }) => {
  const [meetings, setMeetings] = useState<ScheduledMeeting[]>([]);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedMeeting, setSelectedMeeting] = useState<ScheduledMeeting | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { notifySuccess, notifyError } = useNotificationProvider();

  // Fetch scheduled meetings when component mounts
  useEffect(() => {
    getScheduledMeetings();
  }, []);

  // Refresh when refreshTrigger changes (triggered by parent component)
  useEffect(() => {
    if (refreshTrigger) {
      getScheduledMeetings();
    }
  }, [refreshTrigger]);

  // Set up interval to refresh meetings every minute to update join button status
  useEffect(() => {
    const interval = setInterval(() => {
      getScheduledMeetings();
    }, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, []);

  // Fetch scheduled meetings
  const getScheduledMeetings = async () => {
    try {
      const response = await fetchScheduledMeetings();
      if (response?.meetings) {
        // Filter out past meetings (completed non-recurring meetings)
        const now = new Date();
        const filteredMeetings = response.meetings.filter((meeting: ScheduledMeeting) => {
          const meetingEndTime = new Date(meeting.meeting_start_datetime);
          meetingEndTime.setMinutes(meetingEndTime.getMinutes() + meeting.meeting_duration);
          
          // Keep recurring meetings or meetings that haven't ended yet
          return meeting.meeting_recurrence !== 'none' || meetingEndTime > now;
        });
        
        setMeetings(filteredMeetings);
      }
    } catch (error) {
      console.error("[ScheduledMeetings] Failed to fetch scheduled meetings:", error);
      notifyError("Failed to load scheduled meetings");
    }
  };

  // Generate meeting URL
  const getMeetingUrl = (meeting: ScheduledMeeting): string => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/canappsv3/meeting?roomUrl=${encodeURIComponent(meeting.meeting_link)}`;
  };

  // Copy meeting link to clipboard
  const copyMeetingLink = async (meeting: ScheduledMeeting) => {
    try {
      const meetingUrl = getMeetingUrl(meeting);
      await navigator.clipboard.writeText(meetingUrl);
      notifySuccess("Meeting link copied to clipboard!");
    } catch (error) {
      console.error("[ScheduledMeetings] Failed to copy link:", error);
      notifyError("Failed to copy link");
    }
  };

  // Delete a scheduled meeting
  const deleteMeeting = async (meetingId: number) => {
    try {
      await deleteScheduledMeeting(meetingId);
      await getScheduledMeetings();
      notifySuccess("Meeting deleted successfully!");
    } catch (error) {
      console.error("[ScheduledMeetings] Failed to delete meeting:", error);
      notifyError("Failed to delete meeting");
    }
  };

  // Open edit modal
  const handleEditMeeting = (meeting: ScheduledMeeting) => {
    setSelectedMeeting(meeting);
    setEditModalOpen(true);
  };

  // Handle meeting update
  const handleMeetingUpdated = async () => {
    await getScheduledMeetings();
    notifySuccess("Meeting updated successfully!");
  };

  // Define DataGrid columns
  const columns: GridColDef[] = [
    { 
      field: "meeting_name", 
      headerName: "Meeting Name", 
      width: isMobile ? 200 : 300,
      flex: isMobile ? 0 : 1,
    },
    { 
      field: "meeting_start_datetime", 
      headerName: "Start Date", 
      width: isMobile ? 150 : 200,
      flex: isMobile ? 0 : 1,
      renderCell: (params: GridRenderCellParams) => {
        const date = new Date(params.value);
        return date.toLocaleString([], { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit', 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
    },
    { 
      field: "meeting_duration", 
      headerName: "Duration", 
      width: 150,
      hide: isMobile,
      renderCell: (params: GridRenderCellParams) => {
        return formatDuration(params.value);
      }
    },
    { 
      field: "meeting_recurrence", 
      headerName: "Recurrence", 
      width: 120,
      hide: isMobile,
      renderCell: (params: GridRenderCellParams) => {
        return formatRecurrence(params.value);
      }
    },
    { 
      field: "meeting_creator", 
      headerName: "Creator", 
      width: 150,
      hide: isMobile,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: isMobile ? 200 : 320,
      flex: isMobile ? 0 : 0,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const meeting = params.row as ScheduledMeeting;
        const meetingStatus = calculateMeetingStatus(meeting);
        
        // Determine if edit/delete buttons should be enabled based on editable field and meeting status
        const canEdit = meeting.editable && 
                       (meetingStatus.status === 'upcoming' || 
                        (meetingStatus.status === 'can_join' && meetingStatus.timeUntilStart >= 0));
        
        const canDelete = canEdit;
        
        // Determine if meeting is in progress
        const isInProgress = meetingStatus.status === 'in_progress';

        return (
          <Stack direction="row" spacing={1}>
            <Tooltip title={meetingStatus.canJoin ? "Join meeting" : "Cannot join this meeting"}>
              <span>
                <Link
                  href={getMeetingUrl(meeting)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textDecoration: 'none',
                    pointerEvents: meetingStatus.canJoin ? 'auto' : 'none',
                    opacity: meetingStatus.canJoin ? 1 : 0.5,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={!meetingStatus.canJoin}
                    sx={{ 
                      minWidth: isMobile ? '50px' : '80px',
                      fontSize: isMobile ? '0.7rem' : '0.875rem',
                      padding: isMobile ? '4px 8px' : '6px 16px',
                      pointerEvents: 'auto',
                    }}
                  >
                    Join
                  </Button>
                </Link>
              </span>
            </Tooltip>
            
            <Tooltip title={
              !meeting.editable ? "You can only edit meetings you created" :
              isInProgress ? "Cannot edit meeting in progress" :
              canEdit ? "Edit meeting" :
              "Cannot edit past meetings"
            }>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleEditMeeting(meeting)}
                  color="primary"
                  disabled={!canEdit || isInProgress}
                  sx={{
                    padding: isMobile ? '2px' : '8px',
                    minWidth: isMobile ? '32px' : '40px',
                    height: isMobile ? '32px' : '40px',
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            
            <Tooltip title="Copy meeting link">
              <IconButton
                size="small"
                onClick={() => copyMeetingLink(meeting)}
                color="primary"
                sx={{
                  padding: isMobile ? '2px' : '8px',
                  minWidth: isMobile ? '32px' : '40px',
                  height: isMobile ? '32px' : '40px',
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={
              !meeting.editable ? "You can only delete meetings you created" :
              isInProgress ? "Meeting in progress" : 
              canDelete ? "Delete meeting" : 
              "Cannot delete meeting in progress"
            }>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => deleteMeeting(meeting.id)}
                  disabled={!canDelete || isInProgress || !meeting.editable}
                  sx={{ 
                    minWidth: isMobile ? '50px' : '80px',
                    fontSize: isMobile ? '0.7rem' : '0.875rem',
                    padding: isMobile ? '4px 8px' : '6px 16px',
                    opacity: (canDelete && !isInProgress && meeting.editable) ? 1 : 0.5,
                  }}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  // Render the UI
  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        p: 3,
        mb: 3,
      }}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>
        Scheduled Meetings
      </Typography>
      <Paper>
        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={meetings}
            columns={columns}
            pageSize={20}
            disableSelectionOnClick
            getRowId={(row) => row.id}
            localeText={{
              noRowsLabel: 'You have no scheduled meetings.',
            }}
          />
        </Box>
      </Paper>

      {/* Edit Meeting Modal */}
      <EditMeetingModal
        open={editModalOpen}
        meeting={selectedMeeting}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedMeeting(null);
        }}
        onMeetingUpdated={handleMeetingUpdated}
      />

    </Container>
  );
};

export default ScheduledMeetings;
